import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { emergencyService } from '@/services/emergencyService';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import type { SocietyEmergency } from '@/types/emergency';
import { formatDate } from '@/utils/date';

export function EmergencyActiveAlertBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, user, tenant } = useAuthStore();
  const showToast = useUiStore((s) => s.showToast);

  const [activeEmergency, setActiveEmergency] = useState<SocietyEmergency | null>(null);
  const [isAcknowledgedLocally, setIsAcknowledgedLocally] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const societyId = user?.tenantId || (user as any)?.societyId || tenant?.id;

  const fetchActive = async () => {
    if (authState !== 'AUTHENTICATED' || !societyId) return;
    try {
      const res = await emergencyService.getActiveEmergency();
      if (res.data && res.data.status === 'ACTIVE') {
        setActiveEmergency(res.data);
      } else {
        setActiveEmergency(null);
      }
    } catch {
      // Graceful ignore
    }
  };

  useEffect(() => {
    void fetchActive();
    const interval = setInterval(() => {
      void fetchActive();
    }, 20000); // Check every 20s

    return () => clearInterval(interval);
  }, [authState, societyId]);

  // If user is already on the detail page, we don't need duplicate banner
  if (
    !activeEmergency ||
    activeEmergency.status !== 'ACTIVE' ||
    isDismissed ||
    location.pathname.startsWith('/emergencies')
  ) {
    return null;
  }

  const handleAcknowledge = async () => {
    if (!activeEmergency?._id) return;
    setIsAcknowledging(true);
    try {
      await emergencyService.acknowledgeEmergency(activeEmergency._id);
      setIsAcknowledgedLocally(true);
      showToast('Emergency acknowledged. Alert silenced.', 'info');
    } catch (err: any) {
      showToast(err?.message || 'Failed to acknowledge alert', 'error');
    } finally {
      setIsAcknowledging(false);
    }
  };

  const getEmergencyLabel = (type: string) => {
    switch (type) {
      case 'FIRE':
        return 'FIRE ALERT';
      case 'MEDICAL':
        return 'MEDICAL EMERGENCY';
      case 'SECURITY_THREAT':
        return 'SECURITY THREAT';
      case 'GAS_LEAK':
        return 'GAS LEAK HAZARD';
      case 'WATER_FLOODING':
        return 'WATER / FLOODING';
      case 'POWER_ELECTRICAL':
        return 'ELECTRICAL HAZARD';
      case 'CRIME_SUSPICIOUS':
        return 'SUSPICIOUS ACTIVITY';
      default:
        return 'SOCIETY EMERGENCY';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={6}
        sx={{
          bgcolor: '#DC2626',
          color: '#FFFFFF',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderRadius: { xs: 0, sm: 2 },
          my: { xs: 0, sm: 1.5 },
          mx: { xs: 0, sm: 2 },
          border: '2px solid #EF4444',
          boxShadow: '0 0 20px rgba(220, 38, 38, 0.6)',
          animation: 'pulse 2s infinite ease-in-out',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 10px rgba(220, 38, 38, 0.4)' },
            '50%': { boxShadow: '0 0 25px rgba(220, 38, 38, 0.85)' },
            '100%': { boxShadow: '0 0 10px rgba(220, 38, 38, 0.4)' },
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                color: '#DC2626',
                p: 1,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalFireDepartmentIcon fontSize="medium" />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={900} sx={{ letterSpacing: 0.5, lineHeight: 1.2 }}>
                  🚨 {getEmergencyLabel(activeEmergency.emergencyType)}
                </Typography>
                <Typography variant="caption" sx={{ bgcolor: 'rgba(0,0,0,0.25)', px: 1, py: 0.25, borderRadius: 1, fontWeight: 700 }}>
                  {activeEmergency.emergencyNumber}
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} sx={{ opacity: 0.95 }}>
                Location: <strong>{activeEmergency.location}</strong> • Reported at{' '}
                {formatDate(activeEmergency.createdAt, 'HH:mm')} by {activeEmergency.raisedByName} ({activeEmergency.raisedByRole})
              </Typography>
              {activeEmergency.description && (
                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.25 }}>
                  "{activeEmergency.description}"
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}>
            {!isAcknowledgedLocally ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<NotificationsOffIcon />}
                onClick={handleAcknowledge}
                disabled={isAcknowledging}
                sx={{
                  bgcolor: '#FFFFFF',
                  color: '#DC2626',
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  '&:hover': { bgcolor: '#F1F5F9' },
                }}
              >
                {isAcknowledging ? 'Acknowledging...' : 'Stop Alert / Acknowledge'}
              </Button>
            ) : (
              <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 700 }}>
                ✓ Alert Acknowledged
              </Typography>
            )}

            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/emergencies/${activeEmergency._id}`)}
              sx={{
                borderColor: '#FFFFFF',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: '#FFFFFF' },
              }}
            >
              View Details
            </Button>

            <IconButton
              size="small"
              onClick={() => setIsDismissed(true)}
              sx={{ color: '#FFFFFF', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
